/**
 * MHA file parser using ITK-WASM.
 *
 * Converts MHA binary data to vtkImageData for visualization.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import { itkPipelinesBaseUrl } from '@/lib/itk-pipelines';

let imageIoModulePromise: Promise<typeof import('@itk-wasm/image-io')> | undefined;

/** Lazily import ITK-WASM image IO, pointed at the self-hosted pipelines once. */
function loadImageIo(): Promise<typeof import('@itk-wasm/image-io')> {
    imageIoModulePromise ??= import('@itk-wasm/image-io').then(
        (module) => {
            module.setPipelinesBaseUrl(itkPipelinesBaseUrl('image-io'));
            return module;
        },
        (error: unknown) => {
            // Don't cache a failed chunk load: let the next case open retry it.
            imageIoModulePromise = undefined;
            throw error;
        }
    );
    return imageIoModulePromise;
}

/**
 * Parse MHA file data into vtkImageData.
 *
 * @param data - Raw MHA file as ArrayBuffer
 * @returns vtkImageData for VTK.js rendering
 */
export async function parseMha(data: ArrayBuffer): Promise<vtkImageData> {
    const imageIoModule = await loadImageIo();

    // Create a BinaryFile object for ITK-WASM
    const inputFile = {
        data: new Uint8Array(data),
        path: 'volume.mha',
    };

    // Parse with ITK-WASM
    const result = await imageIoModule.metaReadImage(inputFile, {
        webWorker: null,
        noCopy: true,
    });

    // Clean up web worker
    result.webWorker?.terminate();

    // Convert ITK image to VTK.js image data
    const { default: vtkITKHelper } = await import('@kitware/vtk.js/Common/DataModel/ITKHelper');
    return vtkITKHelper.convertItkToVtkImage(result.image);
}
